import {
  makeVar,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { v4 as uuidv4 } from 'uuid'
import { TokenRefreshLink } from 'apollo-link-token-refresh'
import { getPersistedAuthData } from './auth'
import { isAuthTokenExpired } from './jwtToken'
import { setAuthData, deleteAuthData } from '../hooks/useAuth'
import { SERVER_GRAPHQL } from '../constants'
import { getProfilePic } from '../utils'

const persistedAuthData = getPersistedAuthData()

export const apolloAuthData = makeVar({
  authToken: persistedAuthData?.authToken ?? null,
  refreshToken: persistedAuthData?.refreshToken ?? null,
  user: persistedAuthData?.user ?? null,
})

export const apolloOfferingStatusData = makeVar({
  created: false,
})

export const apolloEditStatusData = makeVar({
  originalData: null,
  changes: [],
  published: false,
})

export const setEditStatus = (
  editStatusData = { changes: {}, published: false },
  callback = () => {}
) => {
  apolloEditStatusData(editStatusData)
  // Call the callback function
  callback()
}

export const setChangesEditStatus = (
  editStatusData = { changes: {} },
  callback = () => {}
) => {
  apolloEditStatusData({ ...editStatusData, published: false })
  // Call the callback function
  callback()
}

export const getEditStatus = () => {
  return apolloEditStatusData()
}

export const resetEditStatus = () => {
  apolloEditStatusData({
    originalData: null,
    changes: [],
    published: false,
  })
}

export const setPublishedEditStatus = () => {
  apolloEditStatusData({
    originalData: null,
    changes: [],
    published: true,
  })
}

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        apolloAuthData: {
          read() {
            return apolloAuthData()
          },
        },
        apolloOfferingStatusData: {
          read() {
            return apolloOfferingStatusData()
          },
        },
        apolloEditStatusData: {
          read() {
            return apolloEditStatusData()
          },
        },
      },
    },
  },
})

/**
 * Refresh auth token if it's expired.
 */
const tokenRefreshLink = new TokenRefreshLink({
  accessTokenField: `refreshJwtAuthToken`,
  isTokenValidOrUndefined: () => {
    const { authToken } = apolloAuthData()

    return !isAuthTokenExpired(authToken) || !authToken
  },
  fetchAccessToken: () => {
    const { refreshToken } = apolloAuthData()
    const query = `
      mutation refreshJwtAuthToken($input: RefreshJwtAuthTokenInput!) {
        refreshJwtAuthToken(input: $input) {
          authToken
        }
      }
    `

    return fetch(SERVER_GRAPHQL, {
      method: 'POST',
      mode: 'cors',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          input: {
            clientMutationId: uuidv4(),
            jwtRefreshToken: refreshToken || ``,
          },
        },
      }),
    })
  },
  handleFetch: async (response) => {
    const { user } = apolloAuthData()

    const { authToken } = response
    setAuthData({ ...apolloAuthData(), authToken })

    // Get the profile pic again
    if (user.profilePic) {
      getProfilePic(user.profilePic)
        .then((profilePicURL) => {
          if (profilePicURL) {
            setAuthData({
              ...apolloAuthData(),
              user: { ...apolloAuthData().user, profilePicURL },
            })
          } else {
            throw new Error()
          }
        })
        .catch(() => {
          setAuthData({
            ...apolloAuthData(),
            user: {
              ...apolloAuthData().user,
              profilePicURL: '',
              profilePic: '',
            },
          })
        })
    }
  },
  handleError: (error) => {
    console.error(error)
    deleteAuthData()
  },
})

/**
 * Include auth token in request headers.
 */
const authLink = setContext((_, { headers }) => {
  const { authToken } = apolloAuthData()

  return {
    headers: {
      ...headers,
      authorization: authToken ? `Bearer ${authToken}` : ``,
    },
  }
})

/**
 * Handle errors.
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations
        )}, Path: ${path}`
      )

      if (message.includes('without authentication')) {
        deleteAuthData()
      }
    })
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)
  }
})

/**
 * Update the authToken and refreshToken with the updated tokens
 * sent back in the response headers.
 */
const tokenUpdateLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const {
      response: { headers },
    } = operation.getContext()

    if (headers) {
      const authToken = headers.get('x-jwt-auth')
      const refreshToken = headers.get('x-jwt-refresh')

      if (authToken) {
        setAuthData({ ...apolloAuthData(), authToken })
      }

      if (refreshToken) {
        setAuthData({ ...apolloAuthData(), refreshToken })
      }
    }

    return response
  })
})

/**
 * Handle HTTP requests.
 */
const httpLink = createHttpLink({
  uri: SERVER_GRAPHQL,
  fetch,
})

export const client = new ApolloClient({
  link: ApolloLink.from([
    tokenRefreshLink,
    authLink,
    errorLink,
    tokenUpdateLink,
    httpLink,
  ]),
  cache,
})
